import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { ArrowDropDownIcon, ArrowRightIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { scrollToSelectedAnchor, useCaching } from "utils/common";

import Box from "components/Box";
import Collapse from "components/Collapse";
import { ID as SUB_NAVIGATION_ID } from "components/DealPageLayout/SubNavigation";
import IconButton from "components/IconButton";
import { ID as MAIN_NAVIGATION_ID } from "components/MainNavigation";
import Stack from "components/Stack";
import Text from "components/Text";

import FieldUpdateIcon from "entities/Field/components/FieldUpdateIcon";
import MenuButton from "entities/Package/components/Category/MenuButton";
import Instructions from "entities/Package/components/Instructions";
import Section from "entities/Package/components/Section";
import { DueDiligenceShareableType } from "entities/Package/constants";
import {
  fieldMarkAsSeenBulk,
  IPackageCategory,
  useCompanyPackageWithTabsInformation,
} from "entities/Package/sdk";

export interface ICategory {
  category: IPackageCategory;
  isExpanded: boolean;
  disableEdits: boolean;
  handlePackageUpdate: ({ message }: { message?: string }) => void;
  dataTestid?: string;
  isFocused?: boolean;
  shareablePageType?: DueDiligenceShareableType;
}

const ANCHORS = [MAIN_NAVIGATION_ID, SUB_NAVIGATION_ID];

const Category = ({
  shareablePageType,
  category,
  isExpanded,
  dataTestid,
  disableEdits,
  handlePackageUpdate,
  isFocused = false,
}: ICategory) => {
  const [open, setOpen] = useCaching(isExpanded);

  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId?: string;
    categoryId?: string;
    sectionId?: string;
    fieldId?: string;
  }>();
  const [searchParams] = useSearchParams();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const selectedSectionId = useMemo(() => {
    const sectionId = searchParams.get("sectionId");
    return !_.isNil(sectionId) ? Number(sectionId) : null;
  }, [searchParams]);

  const selectedFieldId = useMemo(() => {
    const fieldId = searchParams.get("fieldId");
    return !_.isNil(fieldId) ? Number(fieldId) : null;
  }, [searchParams]);

  useLayoutEffect(() => {
    // If we have the same sections in different categories, we will scroll to the first.
    if (
      !_.isNil(selectedSectionId) &&
      category.sections
        .map((section) => section.section_id)
        .includes(selectedSectionId)
    ) {
      requestAnimationFrame(() => {
        scrollToSelectedAnchor({
          anchor: selectedSectionId.toString(),
          navigationIds: ANCHORS,
        });
      });
    }
  }, [selectedSectionId, category.sections]);

  useEffect(() => {
    if (!_.isNil(selectedSectionId) && !open) {
      const sectionIsInCategory = category.sections.some(
        (section) => section.section_id === selectedSectionId
      );

      setOpen(sectionIsInCategory);
    }
  }, [selectedSectionId, category, open, setOpen]);

  useEffect(() => {
    if (!_.isNil(selectedFieldId) && !open) {
      const fieldIsInCategory = category.sections.some((section) =>
        section.fields.some((field) => field.field_id === selectedFieldId)
      );

      setOpen(fieldIsInCategory);
    }
  }, [selectedFieldId, category, open, setOpen]);

  const { mutate: updateTabs } = useCompanyPackageWithTabsInformation({
    dealId,
    packageId,
  });

  const areAllNA = useMemo(
    () =>
      _.every(
        _.map(category?.sections, (section) =>
          _.every(section?.fields, { n_a_status: true })
        )
      ),
    [category]
  );

  const handleCategoryMarkAllAsSeen = useCallback(() => {
    const fieldIds = _.flatMap(category.sections, (section) =>
      section.fields.map((field) => field.field_id)
    );

    fieldMarkAsSeenBulk({
      dealId,
      packageId,
      fieldIds,
    }).then(() => {
      updateTabs();
      handlePackageUpdate({
        message: `Updated badges for ${category?.name} have been removed successfully!`,
      });
    });
  }, [dealId, category, handlePackageUpdate, packageId, updateTabs]);

  return (
    <Box id={category.category_id.toString()}>
      <Box
        sx={{
          padding: theme.spacing(0.5, 1),
          backgroundColor: isFocused ? colors.green10 : colors.blue20,
          marginBottom: theme.spacing(1),
          borderRadius: "4px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          data-testid={dataTestid}
        >
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            minHeight="32px"
          >
            <IconButton
              onClick={() => setOpen(!open)}
              size="small"
              dataTestid="dd-category-expand-button"
            >
              {open ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
            </IconButton>
            <Text
              variant="text2"
              fontWeight="500"
              sx={{
                opacity: areAllNA ? 0.5 : 1,
              }}
              data-testid="dd-category-name"
            >
              {category.name}
            </Text>
            {category.updated_field_count > 0 && (
              <FieldUpdateIcon
                tooltip={`Category has ${category.updated_field_count} field${category.updated_field_count > 1 ? "s" : ""} updated`}
                numberOfUpdates={category.updated_field_count}
              />
            )}
          </Stack>
          {!_.isNil(category) && (
            <MenuButton
              category={category}
              packageId={packageId}
              dealId={dealId}
              isShareablePage={
                !_.isUndefined(shareablePageType) &&
                shareablePageType !== DueDiligenceShareableType.CATEGORY
              }
              updatePackage={handlePackageUpdate}
              menuButtonDataTestid="dd-category-actions-button"
              handleCategoryMarkAllAsSeen={handleCategoryMarkAllAsSeen}
            />
          )}
        </Stack>
        <Instructions
          internalInstructions={category?.internal_instructions}
          externalInstructions={category?.external_instructions}
        />
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack
          sx={{
            marginLeft: 3,
            marginBottom: 1,
          }}
          spacing={1}
        >
          {_.uniqBy(category.sections, "section_id").map((section: any) => (
            <Section
              key={section.section_id}
              isFocused={selectedSectionId === section.section_id}
              section={section}
              categoryId={category.category_id}
              isExpanded={isExpanded}
              updatePackage={handlePackageUpdate}
              dataTestid="dd-section"
              disableEdits={disableEdits}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default Category;
